import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { UserStat, updateUserSubscription } from '../../../../services/adminService';

interface PlanChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: UserStat;
    onSuccess: () => void;
}

type SubscriptionStatus = 'free' | 'basic' | 'pro' | 'inactive';

const PlanChangeModal: React.FC<PlanChangeModalProps> = ({ isOpen, onClose, user, onSuccess }) => {
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionStatus>(user.subscription_status);
    const [isLoading, setIsLoading] = useState(false);

    const plans = [
        {
            value: 'free' as SubscriptionStatus,
            label: 'Free',
            description: 'Plano gratuito com recursos limitados',
            color: 'gray',
            icon: 'lock_open',
        },
        {
            value: 'basic' as SubscriptionStatus,
            label: 'Basic',
            description: 'Plano básico com recursos essenciais',
            color: 'blue',
            icon: 'star',
        },
        {
            value: 'pro' as SubscriptionStatus,
            label: 'Pro',
            description: 'Plano completo com todos os recursos',
            color: 'green',
            icon: 'workspace_premium',
        },
        {
            value: 'inactive' as SubscriptionStatus,
            label: 'Inativo',
            description: 'Conta desativada ou suspensa',
            color: 'red',
            icon: 'block',
        },
    ];

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const result = await updateUserSubscription(user.id, selectedPlan);

            if (result.success) {
                toast.success('Plano atualizado com sucesso!');
                onSuccess();
            } else {
                toast.error('Erro ao atualizar plano');
                console.error(result.error);
            }
        } catch (error) {
            console.error('Error updating plan:', error);
            toast.error('Erro ao atualizar plano');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop - mais opaco */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/70 dark:bg-black/85 backdrop-blur-md z-[60]"
            />

            {/* Modal */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose();
                }}
            >
                <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full border-2 border-gray-200 dark:border-gray-700 shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800">
                        <h3 className="text-xl font-bold text-text-dark dark:text-white">
                            Alterar Plano de Assinatura
                        </h3>
                        <p className="text-sm text-text-dark/70 dark:text-gray-400 mt-1">
                            {user.full_name || 'Usuário'}
                        </p>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            {plans.map((plan) => (
                                <button
                                    key={plan.value}
                                    onClick={() => setSelectedPlan(plan.value)}
                                    className={`p-4 rounded-xl border-2 transition-all text-left ${selectedPlan === plan.value
                                        ? `border-${plan.color}-500 bg-${plan.color}-500/10`
                                        : 'border-border-light dark:border-border-dark hover:border-primary/50'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={`material-symbols-outlined text-${plan.color}-500 text-2xl`}>
                                            {plan.icon}
                                        </span>
                                        <div>
                                            <h4 className="font-semibold text-text-dark dark:text-white mb-1">
                                                {plan.label}
                                            </h4>
                                            <p className="text-xs text-text-dark/70 dark:text-gray-400">
                                                {plan.description}
                                            </p>
                                        </div>
                                    </div>
                                    {selectedPlan === plan.value && (
                                        <div className="mt-2 flex items-center gap-1 text-primary text-sm">
                                            <span className="material-symbols-outlined text-sm">check_circle</span>
                                            <span>Selecionado</span>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Warning */}
                        {selectedPlan !== user.subscription_status && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6"
                            >
                                <div className="flex items-start gap-3">
                                    <span className="material-symbols-outlined text-orange-500">warning</span>
                                    <div>
                                        <p className="text-sm text-text-dark dark:text-text-light font-medium mb-1">
                                            Atenção
                                        </p>
                                        <p className="text-xs text-text-dark/70 dark:text-text-light/70">
                                            Você está prestes a alterar o plano de <strong>{user.subscription_status}</strong> para{' '}
                                            <strong>{selectedPlan}</strong>. Esta ação terá efeito imediato.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-border-light dark:border-border-dark px-6 py-4 flex items-center justify-end gap-3">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-4 py-2 rounded-lg text-text-dark dark:text-text-light hover:bg-surface-dark/10 dark:hover:bg-surface-light/10 transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading || selectedPlan === user.subscription_status}
                            className="px-6 py-2 rounded-lg bg-primary hover:bg-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isLoading && (
                                <span className="material-symbols-outlined animate-spin text-sm">sync</span>
                            )}
                            {isLoading ? 'Salvando...' : 'Salvar Alteração'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </>
    );
};

export default PlanChangeModal;
